import { JSX, PureComponent } from "react";
import orderBy from "lodash/orderBy";

import { QESButton, QESTextInput, QESSelect, QESCheckbox } from "@app/shared/qes-standards";
import { MultiselectItem } from "@app/shared/qes-standards/forms/Multiselect";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { LoadingSpinner } from "@app/shared";

import { ClientListItem, ClientService } from "@app/client";
import { TemplateMetadataModel, TemplateCollectionMetadataModelWithActiveProjects, TemplateService } from "@app/admin/manage-templates";
import { FlyoutContextOutgoingProps, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";
import { MinimalUser } from "../user-administration/userModel";

interface CreatePanelProps {
  currentUserid: number;
  defaultClient: ClientListItem;
  handleSave: (onClose: () => void, templateCollectionName: string, templateCount: number, isActive: boolean, selectedClientId: number, editUserIds: number[], templateId?: number) => void;
  allowClientEdit: boolean;
  loading: boolean;
  template?: TemplateCollectionMetadataModelWithActiveProjects;
}

interface CreatePanelState {
  authorizedUsers: MinimalUser[];
  clientOptions: JSX.Element[];
  isActive: boolean;
  templateCount: number;
  loading: boolean;
  templateCollectionName: string;
  templateList: TemplateMetadataModel [];
  nameIsValid: boolean;
  nameIsValidText: string;
  selectedClientId: number;
  selectedUsers: MinimalUser[];
  userOptions: { [key: string]: MultiselectItem };
}

class WrappedCreateEditPanelContent extends PureComponent<CreatePanelProps & FlyoutContextOutgoingProps, CreatePanelState> {

  private templateService = new TemplateService();
  private clientService = new ClientService();
  private nameValidationInterval: number = null;

  constructor(props: CreatePanelProps & FlyoutContextOutgoingProps) {
    super(props);

    const defaultTemplateInfo = {
      category: "<Category Goes Here>",
      clientId: props.defaultClient.clientId,
      enabled: true,
      isActive: true,
      templateCollectionName: "<Collection Name Goes Here>",
      users: [] as MinimalUser[]
    }

    this.state = {
      authorizedUsers: [],
      clientOptions: [],
      isActive: defaultTemplateInfo.isActive,
      loading: false,
      nameIsValid: defaultTemplateInfo.templateCollectionName.length > 0,
      nameIsValidText: "",
      selectedClientId: defaultTemplateInfo.clientId,
      selectedUsers: defaultTemplateInfo.users,
      templateCollectionName: defaultTemplateInfo.templateCollectionName,
      templateCount: 0,
      templateList: [],
      userOptions: {}
    };
  }

  public async componentDidMount() {
    this.setState({ loading: true });

    let clients: ClientListItem[] = [this.props.defaultClient];

    if (this.props.allowClientEdit) {
      clients = await this.clientService.getActiveClientsList();
    }

    this.handleClientChange(this.state.selectedClientId);

    this.setState({
      clientOptions: clients.map(x => <option key={x.clientId} value={x.clientId}>{x.name}</option>),
      loading: false,
    });
  }

  private handleClientChange = async (clientId: number) => {
    this.setState({
    });

    let authorizedUsers: MinimalUser[] = await this.templateService.getUsersAuthorizedForEdit(clientId);
    authorizedUsers = orderBy(authorizedUsers, (x) => x.userName, "asc");

    const selectedUsers = this.state.selectedUsers;
    if (!this.props.template?.templateCollectionId && !selectedUsers.some(x => x.userId === this.props.currentUserid)) {
      const currentUser = authorizedUsers.find(x => x.userId === this.props.currentUserid);
      if (!!currentUser) {
        selectedUsers.push(currentUser);
      }
    }

    this.setState({
      authorizedUsers: authorizedUsers,
      selectedUsers: selectedUsers.filter(su => authorizedUsers.some(u => u.userId === su.userId)),
      userOptions: this.buildUserOptions(authorizedUsers, this.state.selectedUsers),
    });
  }

  private buildUserOptions(users: MinimalUser[], selectedUsers: MinimalUser[]): { [key: string]: MultiselectItem } {
    const userOptions: { [key: string]: MultiselectItem } = {};
    if (users) {
      users.forEach((u) => Object.assign(userOptions,
        {
          [u.userId]: {
            currentState: selectedUsers.find(x => x.userId === u.userId) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            displayValue: u.userName,
          }
        }));
    }
    return userOptions;
  }


  public componentWillUnmount() {
    window.clearInterval(this.nameValidationInterval);
  }

  private validateName = async (templateCollectionName: string, clientId: number) => {
    window.clearInterval(this.nameValidationInterval);
    this.nameValidationInterval = window.setTimeout(async () => {
      if (!templateCollectionName) {
        this.setState({
          nameIsValid: false,
          nameIsValidText: ""
        });
        return;
      } else if (await this.templateService.templateNameExists(templateCollectionName.trim(), clientId, this.props.template?.templateCollectionId)) {
        this.setState({
          nameIsValid: false,
          nameIsValidText: "This templateCollectionName has already been used."
        });
      } else {
        this.setState({
          nameIsValid: true,
          nameIsValidText: ""
        });
      }
    }, 500);
  }

  public render() {
    return (
      <>
        {this.props.loading || this.state.loading ? <LoadingSpinner /> :
          <>
            <QESTextInput
              testId={"create-manage-template-panel-content__name"}
              className="col-4"
              containerClassName="mb-2"
              label="Template Collection Name"
              value={this.state.templateCollectionName}
              onChange={(e) => {
                this.setState({ templateCollectionName: e.target.value });
                this.validateName(e.target.value, this.state.selectedClientId);
              }}
              isValid={this.state.nameIsValid}
              errorText={this.state.nameIsValidText}
              required={true}
              wasFocusedExternally={!!this.props.template}
            />
            <QESSelect
              testId="create-manage-template-panel-content__client"
              className="col-4 mb-2"
              label="Client"
              value={this.state.selectedClientId}
              required={true}
              isValid={!!this.state.selectedClientId.toString()}
              disabled={!this.props.allowClientEdit}
              onChange={(e) => {
                this.setState({ selectedClientId: parseInt(e.target.value) });
                this.handleClientChange(parseInt(e.target.value));
                this.validateName(this.state.templateCollectionName, parseInt(e.target.value));
              }}
            >
              {this.state.clientOptions}
            </QESSelect>
            
            <QESCheckbox
              containerClassName="mb-2"
              testId={"create-manage-template-panel-content__active"}
              label="Is Active"
              checked={this.state.isActive}
              onChange={(e) => this.setState({ isActive: e.target.checked })}
            />

            <FlyoutFooter
              submitButton={<QESButton
                qesTestId="create-manage-template-panel-content__confirm"
                className="mt10"
                //disabled={!this.state.nameIsValid || this.state.selectedUsers.length < 2}
                onClick={() => {
                  this.props.handleSave(this.props.handleCloseFlyout, this.state.templateCollectionName.trim(), this.state.templateCount, this.state.isActive, this.state.selectedClientId, this.state.selectedUsers.map(x => x.userId), this.props.template?.templateCollectionId);
                }}
              >Save</QESButton>
              }
            />
          </>
        }
      </>
    );
  }
}

const CreateEditPanelContent = withFlyoutContext(WrappedCreateEditPanelContent);
export default CreateEditPanelContent;
